import { useCallback, useContext, useMemo, useState } from "react"
import { cn } from "@/lib/utils"
import { format } from "date-fns"
import { toast } from "sonner"
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  ArrowUpDown,
  Search,
  Check,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { changeActiveTab, TAB_TYPES, TabType } from "@/reducers/tabsSlice"
import { changeActiveChat } from "@/reducers/chatsSlice"
import { selectChat } from "@/reducers/chatsSliceSelectors"
import useChatInfo from "@/hooks/useChatInfo"
import ImageGallery from "@/components/photoView/ImageGallery"
import PDFViewer from "@/components/pdfView/PDFViewer"
import {
  useCmrReportData,
  CmrReportRow,
  CmrDocumentMetadata,
  UnlinkedDocument,
  UnlinkedAnnotationMap,
} from "./useCmrReportData"
import AdHocCommands from "@/api/adHocCommands"
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  SortingState,
  useReactTable,
  FilterFn,
} from "@tanstack/react-table"
import { rankItem } from "@tanstack/match-sorter-utils"
import { useSelector } from "react-redux"
import { RootState } from "@/reducers/store"
import { XmppContext } from "@/stream/xmppClient"
import { findProfile } from "@/reducers/profilesSliceSelectors"
import { Thumbnail, isPDF, formatTime24h, formatDateShort } from "./Thumbnail"
import { resolveDriverDisplayName } from "./resolveDriverDisplayName"
import { useCmrDocumentPreview } from "./useCmrDocumentPreview"
import { useTranslation } from "react-i18next"
import { getDateFnsLocale } from "@/lib/i18n/format"
import { useCmrReportMode } from "./useCmrReportMode"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({ itemRank })
  return itemRank.passed
}

function DriverCell({
  row,
  onNavigateToChat,
}: {
  row: CmrReportRow
  onNavigateToChat: (jid: string) => void
}) {
  const { t } = useTranslation()
  const profile = useAppSelector(findProfile(row.driverJid))
  const displayName =
    (profile && `${profile.firstName} ${profile.lastName}`) ||
    row.driverJid ||
    t("reports.shared.unknown")

  if (!row.chatJid) {
    return <span className="text-muted-foreground">{displayName}</span>
  }

  return (
    <button
      className="text-left font-medium text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
      onClick={() => onNavigateToChat(row.chatJid)}
    >
      {displayName}
    </button>
  )
}

/**
 * Compute display names for all rows so that the table accessor
 * can use them for filtering and sorting.
 */
function useRowsWithDriverNames(
  data: CmrReportRow[],
): CmrReportRowWithDisplay[] {
  const chats = useSelector((state: RootState) => state.chats)
  const profiles = useSelector((state: RootState) => state.profiles)
  const accountChats = useSelector((state: RootState) => state.accountChats)
  return useMemo(() => {
    return data.map((row) => ({
      ...row,
      driverDisplayName: resolveDriverDisplayName({
        chatJid: row.chatJid,
        driverJid: row.driverJid,
        chats,
        profiles,
        accountChats,
      }),
      deliveryOrderDisplayName: (row.deliveryOrderFileName ?? "").replace(
        /\.[^.]+$/,
        "",
      ),
    }))
  }, [data, chats, profiles, accountChats])
}

type CmrReportRowWithDisplay = CmrReportRow & {
  driverDisplayName: string
  deliveryOrderDisplayName: string
}

/** Get deduplicated linked documents for a row */
function getUniqueLinkedDocs(row: CmrReportRow) {
  return row.linkedDocuments.filter(
    (doc, i, arr) =>
      arr.findIndex((d) => d.cmrFileUrl === doc.cmrFileUrl) === i,
  )
}

/** GPS accounts have no processed status: drop that column without a handler. */
function withoutProcessed<T>(
  columns: ColumnDef<T>[],
  onToggleProcessed: unknown,
): ColumnDef<T>[] {
  return onToggleProcessed
    ? columns
    : columns.filter((column) => column.id !== "processed")
}

function ReportTable({
  data,
  onNavigateToChat,
  onImageClick,
  onPDFClick,
  onToggleProcessed,
}: {
  data: CmrReportRow[]
  onNavigateToChat: (jid: string) => void
  onImageClick: (
    clickedUrl: string,
    allImageDocs: Array<{
      url: string
      metadata?: CmrDocumentMetadata
      messageId?: string
      chatJid?: string
      driverJid?: string
      unlinkInfo?: {
        deliveryOrderMessageId: string
        messageRoomJid: string
      }
    }>,
  ) => void
  onPDFClick: (
    pdfUrl: string,
    metadata?: CmrDocumentMetadata,
    reExtractInfo?: {
      messageId: string
      chatJid?: string
      driverJid?: string
      unlinkInfo?: {
        deliveryOrderMessageId: string
        messageRoomJid: string
      }
    },
  ) => void
  onToggleProcessed?: (row: CmrReportRow) => void
}) {
  const { t } = useTranslation()
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState("")

  const rowsWithNames = useRowsWithDriverNames(data)

  const columns = useMemo<ColumnDef<CmrReportRowWithDisplay>[]>(
    () => [
      {
        accessorKey: "driverDisplayName",
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("reports.cmrReport.columns.driver")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <DriverCell row={row.original} onNavigateToChat={onNavigateToChat} />
        ),
      },
      {
        accessorKey: "deliveryOrderDisplayName",
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("reports.cmrReport.columns.deliveryOrder")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const doTimestamp = row.original.linkedDocuments[0]?.timestamp
          const doDate = formatDateShort(doTimestamp)
          const doTime = formatTime24h(doTimestamp)
          return (
            <div className="flex items-center gap-3">
              <Thumbnail
                url={row.original.deliveryOrderFileUrl}
                fileName={row.original.deliveryOrderFileName}
                onClick={() => {
                  const url = row.original.deliveryOrderFileUrl
                  if (isPDF(url)) {
                    onPDFClick(url)
                  } else {
                    onImageClick(url, [{ url }])
                  }
                }}
              />
              <div className="flex flex-col gap-0.5 text-xs">
                <span className="font-medium">
                  {row.original.deliveryOrderDisplayName}
                </span>
                {doDate && (
                  <span className="text-muted-foreground">
                    {doDate} {doTime}
                  </span>
                )}
              </div>
            </div>
          )
        },
      },
      {
        id: "linkedDocuments",
        header: t("reports.cmrReport.columns.linkedCmrDocuments"),
        cell: ({ row }) => {
          const uniqueDocs = getUniqueLinkedDocs(row.original)
          const imageDocs = uniqueDocs
            .filter((doc) => !isPDF(doc.cmrFileUrl))
            .map((doc) => ({
              url: doc.cmrFileUrl,
              metadata: row.original.metadataByUrl[doc.cmrFileUrl],
              messageId: doc.cmrMessageId,
              chatJid: row.original.chatJid,
              driverJid: row.original.driverJid,
              unlinkInfo: {
                deliveryOrderMessageId: row.original.deliveryOrderMessageId,
                messageRoomJid: row.original.chatJid,
              },
            }))

          return (
            <div className="flex flex-wrap gap-2">
              {uniqueDocs.map((doc) => (
                <Thumbnail
                  key={doc.cmrMessageId}
                  url={doc.cmrFileUrl}
                  fileName={doc.cmrFileName}
                  documentType={
                    row.original.metadataByUrl[doc.cmrFileUrl]
                      ?.extractedMetadata?.documentType
                  }
                  timestamp={doc.timestamp}
                  onClick={() => {
                    if (isPDF(doc.cmrFileUrl)) {
                      onPDFClick(
                        doc.cmrFileUrl,
                        row.original.metadataByUrl[doc.cmrFileUrl],
                        {
                          messageId: doc.cmrMessageId,
                          chatJid: row.original.chatJid,
                          driverJid: row.original.driverJid,
                          unlinkInfo: {
                            deliveryOrderMessageId:
                              row.original.deliveryOrderMessageId,
                            messageRoomJid: row.original.chatJid,
                          },
                        },
                      )
                    } else {
                      onImageClick(doc.cmrFileUrl, imageDocs)
                    }
                  }}
                />
              ))}
            </div>
          )
        },
      },
      {
        id: "processed",
        header: t("reports.cmrReport.columns.processed"),
        cell: ({ row }) => {
          const isProcessed = !!row.original.annotation
          return (
            <button
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded border transition-colors",
                isProcessed
                  ? "border-green-500 bg-green-500 text-white"
                  : "border-muted-foreground/30 hover:border-green-400 hover:bg-green-50 dark:hover:bg-green-950",
              )}
              onClick={() => onToggleProcessed?.(row.original)}
              title={
                isProcessed
                  ? t("reports.cmrReport.processedBy", {
                      by: row.original.annotation!.processedBy,
                      at: row.original.annotation!.processedAt,
                    })
                  : t("reports.cmrReport.markAsProcessed")
              }
            >
              {isProcessed && <Check className="h-4 w-4" />}
            </button>
          )
        },
      },
    ],
    [onNavigateToChat, onImageClick, onPDFClick, onToggleProcessed, t],
  )
  const visibleColumns = useMemo(
    () => withoutProcessed(columns, onToggleProcessed),
    [columns, onToggleProcessed],
  )

  const table = useReactTable<CmrReportRowWithDisplay>({
    data: rowsWithNames,
    columns: visibleColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    globalFilterFn: fuzzyFilter,
    onGlobalFilterChange: setGlobalFilter,
    state: {
      sorting,
      globalFilter,
    },
  })

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder={t("reports.cmrReport.searchLinked")}
          value={globalFilter ?? ""}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="pl-8"
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {data.length === 0
                    ? t("reports.cmrReport.noLinkedDeliveries")
                    : t("reports.shared.noSearchResults")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function UnlinkedDriverCell({
  doc,
  onNavigateToChat,
  driverJid,
}: {
  doc: UnlinkedDocument
  onNavigateToChat: (jid: string) => void
  driverJid: string
}) {
  const { t } = useTranslation()
  const profile = useAppSelector(findProfile(driverJid))
  const displayName =
    (profile && `${profile.firstName} ${profile.lastName}`) ||
    driverJid ||
    t("reports.shared.unknown")

  if (!doc.chatJid) {
    return <span className="text-muted-foreground">{displayName}</span>
  }

  return (
    <button
      className="text-left font-medium text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
      onClick={() => onNavigateToChat(doc.chatJid!)}
    >
      {displayName}
    </button>
  )
}

type UnlinkedGroup = {
  driverJid: string
  chatJid: string
  plates: string
  docs: UnlinkedDocument[]
}

function groupUnlinkedByDriver(documents: UnlinkedDocument[]): UnlinkedGroup[] {
  const map = new Map<string, UnlinkedGroup>()
  for (const doc of documents) {
    const key = doc.driverJid || doc.messageId
    const existing = map.get(key)
    if (existing) {
      existing.docs.push(doc)
    } else {
      map.set(key, {
        driverJid: doc.driverJid || "",
        chatJid: doc.chatJid || "",
        plates: doc.driverContext?.plates || "",
        docs: [doc],
      })
    }
  }
  return Array.from(map.values())
}

/**
 * Stable identity for an unlinked driver group, used as the annotation key.
 * Mirrors the map key in {@link groupUnlinkedByDriver}: driver JID, or the first
 * document's messageId when the group has no resolvable driver.
 */
function unlinkedGroupKey(group: UnlinkedGroup): string {
  return group.driverJid || group.docs[0]?.messageId || ""
}

type UnlinkedGroupWithDisplay = UnlinkedGroup & {
  driverDisplayName: string
  documentTypes: string
}

function useUnlinkedGroupsWithDisplay(
  documents: UnlinkedDocument[],
): UnlinkedGroupWithDisplay[] {
  const chats = useSelector((state: RootState) => state.chats)
  const profiles = useSelector((state: RootState) => state.profiles)
  const accountChats = useSelector((state: RootState) => state.accountChats)
  return useMemo(() => {
    const groups = groupUnlinkedByDriver(documents)
    return groups.map((group) => {
      const driverDisplayName = resolveDriverDisplayName({
        chatJid: group.chatJid,
        driverJid: group.driverJid,
        chats,
        profiles,
        accountChats,
      })
      const documentTypes = group.docs
        .map((d) => d.extractedMetadata?.documentType)
        .filter(Boolean)
        .join(", ")
      return { ...group, driverDisplayName, documentTypes }
    })
  }, [documents, chats, profiles, accountChats])
}

function computeDocumentTypeStats(
  documents: UnlinkedDocument[],
  unknownType: string,
): Array<{ type: string; count: number }> {
  const counts = new Map<string, number>()
  for (const doc of documents) {
    const type = doc.extractedMetadata?.documentType || unknownType
    counts.set(type, (counts.get(type) || 0) + 1)
  }
  return Array.from(counts.entries())
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count)
}

function UnlinkedDocumentsSection({
  documents,
  unlinkedAnnotations,
  onNavigateToChat,
  onImageClick,
  onPDFClick,
  onToggleProcessed,
}: {
  documents: UnlinkedDocument[]
  unlinkedAnnotations: UnlinkedAnnotationMap
  onNavigateToChat: (jid: string) => void
  onImageClick: (
    clickedUrl: string,
    allImageDocs: Array<{
      url: string
      metadata?: CmrDocumentMetadata
      messageId?: string
      chatJid?: string
    }>,
  ) => void
  onPDFClick: (
    pdfUrl: string,
    metadata?: CmrDocumentMetadata,
    reExtractInfo?: { messageId: string; chatJid?: string; driverJid?: string },
  ) => void
  onToggleProcessed?: (group: UnlinkedGroup) => void
}) {
  const { t } = useTranslation()
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState("")

  const groupsWithDisplay = useUnlinkedGroupsWithDisplay(documents)
  const unknownType = t("reports.shared.unknown")
  const stats = useMemo(
    () => computeDocumentTypeStats(documents, unknownType),
    [documents, unknownType],
  )

  const columns = useMemo<ColumnDef<UnlinkedGroupWithDisplay>[]>(
    () => [
      {
        accessorKey: "driverDisplayName",
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("reports.cmrReport.columns.driver")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <UnlinkedDriverCell
            doc={row.original.docs[0]}
            driverJid={row.original.driverJid}
            onNavigateToChat={onNavigateToChat}
          />
        ),
      },
      {
        accessorKey: "documentTypes",
        header: t("reports.cmrReport.columns.documents"),
        cell: ({ row }) => {
          const group = row.original
          const imageDocs = group.docs
            .filter((d) => d.fileUrl && !isPDF(d.fileUrl!))
            .map((d) => ({
              url: d.fileUrl!,
              metadata: {
                extractedMetadata: d.extractedMetadata,
                driverContext: d.driverContext,
              } as CmrDocumentMetadata,
              messageId: d.messageId,
              chatJid: d.chatJid,
              driverJid: d.driverJid,
            }))

          return (
            <div className="flex flex-wrap gap-2">
              {group.docs.map((doc) => {
                const metadata: CmrDocumentMetadata = {
                  extractedMetadata: doc.extractedMetadata,
                  driverContext: doc.driverContext,
                }
                const fileUrl = doc.fileUrl

                return fileUrl ? (
                  <Thumbnail
                    key={doc.messageId}
                    url={fileUrl}
                    fileName={
                      doc.extractedMetadata?.documentType || doc.messageId
                    }
                    documentType={doc.extractedMetadata?.documentType}
                    timestamp={doc.timestamp}
                    onClick={() => {
                      if (isPDF(fileUrl)) {
                        onPDFClick(fileUrl, metadata, {
                          messageId: doc.messageId,
                          chatJid: doc.chatJid,
                          driverJid: doc.driverJid,
                        })
                      } else {
                        onImageClick(fileUrl, imageDocs)
                      }
                    }}
                  />
                ) : (
                  <span
                    key={doc.messageId}
                    className="text-xs text-muted-foreground"
                  >
                    {doc.messageId}
                  </span>
                )
              })}
            </div>
          )
        },
      },
      {
        id: "processed",
        header: t("reports.cmrReport.columns.processed"),
        cell: ({ row }) => {
          const isProcessed =
            !!unlinkedAnnotations[unlinkedGroupKey(row.original)]
          return (
            <button
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded border transition-colors",
                isProcessed
                  ? "border-green-500 bg-green-500 text-white"
                  : "border-muted-foreground/30 hover:border-green-400 hover:bg-green-50 dark:hover:bg-green-950",
              )}
              onClick={() => onToggleProcessed?.(row.original)}
              title={
                isProcessed
                  ? t("reports.cmrReport.processedBy", {
                      by: unlinkedAnnotations[unlinkedGroupKey(row.original)]!
                        .processedBy,
                      at: unlinkedAnnotations[unlinkedGroupKey(row.original)]!
                        .processedAt,
                    })
                  : t("reports.cmrReport.markAsProcessed")
              }
            >
              {isProcessed && <Check className="h-4 w-4" />}
            </button>
          )
        },
      },
    ],
    [
      onNavigateToChat,
      onImageClick,
      onPDFClick,
      onToggleProcessed,
      unlinkedAnnotations,
      t,
    ],
  )
  const visibleColumns = useMemo(
    () => withoutProcessed(columns, onToggleProcessed),
    [columns, onToggleProcessed],
  )

  const table = useReactTable<UnlinkedGroupWithDisplay>({
    data: groupsWithDisplay,
    columns: visibleColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    globalFilterFn: fuzzyFilter,
    onGlobalFilterChange: setGlobalFilter,
    state: { sorting, globalFilter },
  })

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-4 flex flex-wrap gap-2">
          {stats.map(({ type, count }) => (
            <span
              key={type}
              className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium"
            >
              {type}: {count}
            </span>
          ))}
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder={t("reports.cmrReport.searchUnlinked")}
          value={globalFilter ?? ""}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="pl-8"
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {t("reports.shared.noSearchResults")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

type ReportSubTab = "linked" | "unlinked"

function LinkedUnlinkedTabs({
  data,
  unlinkedDocuments,
  unlinkedAnnotations,
  onNavigateToChat,
  onImageClick,
  onPDFClick,
  onToggleProcessed,
  onToggleUnlinkedProcessed,
}: {
  data: CmrReportRow[]
  unlinkedDocuments: UnlinkedDocument[]
  unlinkedAnnotations: UnlinkedAnnotationMap
  onNavigateToChat: (jid: string) => void
  onImageClick: (
    clickedUrl: string,
    allImageDocs: Array<{
      url: string
      metadata?: CmrDocumentMetadata
      messageId?: string
      chatJid?: string
      driverJid?: string
      unlinkInfo?: {
        deliveryOrderMessageId: string
        messageRoomJid: string
      }
    }>,
  ) => void
  onPDFClick: (
    pdfUrl: string,
    metadata?: CmrDocumentMetadata,
    reExtractInfo?: {
      messageId: string
      chatJid?: string
      driverJid?: string
      unlinkInfo?: {
        deliveryOrderMessageId: string
        messageRoomJid: string
      }
    },
  ) => void
  onToggleProcessed?: (row: CmrReportRow) => void
  onToggleUnlinkedProcessed?: (group: UnlinkedGroup) => void
}) {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<ReportSubTab>("linked")

  const totalLinkedDocs = useMemo(
    () => data.reduce((sum, row) => sum + getUniqueLinkedDocs(row).length, 0),
    [data],
  )

  return (
    <div>
      <div className="mb-4 text-sm text-muted-foreground">
        {unlinkedDocuments.length > 0
          ? t("reports.cmrReport.summaryWithUnlinked", {
              deliveries: t("reports.cmrReport.deliveries", {
                count: data.length,
              }),
              linked: t("reports.cmrReport.linkedDocuments", {
                count: totalLinkedDocs,
              }),
              unlinked: unlinkedDocuments.length,
            })
          : t("reports.cmrReport.summary", {
              deliveries: t("reports.cmrReport.deliveries", {
                count: data.length,
              }),
              linked: t("reports.cmrReport.linkedDocuments", {
                count: totalLinkedDocs,
              }),
            })}
      </div>
      <div className="mb-4 flex gap-1 border-b">
        <button
          className={cn(
            "px-3 py-1.5 text-sm font-medium transition-colors",
            activeTab === "linked"
              ? "border-b-2 border-primary text-primary"
              : "text-muted-foreground hover:text-foreground",
          )}
          onClick={() => setActiveTab("linked")}
        >
          {t("reports.cmrReport.tabs.linked")}
        </button>
        <button
          className={cn(
            "px-3 py-1.5 text-sm font-medium transition-colors",
            activeTab === "unlinked"
              ? "border-b-2 border-primary text-primary"
              : "text-muted-foreground hover:text-foreground",
          )}
          onClick={() => setActiveTab("unlinked")}
        >
          {t("reports.cmrReport.tabs.unlinked", {
            number: unlinkedDocuments.length,
          })}
        </button>
      </div>
      {activeTab === "linked" && (
        <ReportTable
          data={data}
          onNavigateToChat={onNavigateToChat}
          onImageClick={onImageClick}
          onPDFClick={onPDFClick}
          onToggleProcessed={onToggleProcessed}
        />
      )}
      {activeTab === "unlinked" && (
        <UnlinkedDocumentsSection
          documents={unlinkedDocuments}
          unlinkedAnnotations={unlinkedAnnotations}
          onNavigateToChat={onNavigateToChat}
          onImageClick={onImageClick}
          onPDFClick={onPDFClick}
          onToggleProcessed={onToggleUnlinkedProcessed}
        />
      )}
    </div>
  )
}

export function CmrReport({
  onNavigateToChat,
}: {
  onNavigateToChat: (jid: string, tab?: TabType) => void
}) {
  const { t } = useTranslation()
  const { client } = useContext(XmppContext)
  const { readOnly } = useCmrReportMode()
  const [selectedDate, setSelectedDate] = useState(() => new Date())
  const {
    data,
    unlinkedDocuments,
    unlinkedAnnotations,
    isLoading,
    error,
    refetch,
    updateAnnotation,
    updateUnlinkedAnnotation,
  } = useCmrReportData(selectedDate)
  const dispatch = useAppDispatch()
  const preview = useCmrDocumentPreview()

  const goToPreviousDay = useCallback(() => {
    setSelectedDate((prev) => {
      const next = new Date(prev)
      next.setDate(next.getDate() - 1)
      return next
    })
  }, [])

  const goToNextDay = useCallback(() => {
    setSelectedDate((prev) => {
      const next = new Date(prev)
      next.setDate(next.getDate() + 1)
      if (next > new Date()) return prev
      return next
    })
  }, [])

  const handleNavigateToChat = useCallback(
    (jid: string) => {
      dispatch(changeActiveChat({ activeChatJid: jid }))
      dispatch(changeActiveTab({ activeTab: TAB_TYPES.CHATS }))
      onNavigateToChat(jid, TAB_TYPES.CHATS)
    },
    [dispatch, onNavigateToChat],
  )

  const handleToggleProcessed = useCallback(
    (row: CmrReportRow) => {
      if (!client) return
      const isProcessed = !!row.annotation
      const doId = row.deliveryOrderMessageId
      const reportDate = format(selectedDate, "yyyy-MM-dd")

      // Optimistic update
      const newAnnotation = isProcessed
        ? undefined
        : {
            processedBy: client.jid?.toString() || "",
            processedAt: new Date().toISOString(),
            chatJid: row.chatJid,
            note: "",
          }
      updateAnnotation(doId, newAnnotation)

      // Fire and forget, revert on error
      const request = isProcessed
        ? AdHocCommands.cmrOrderAnnotation(client, "remove", [doId], reportDate)
        : AdHocCommands.cmrOrderAnnotation(client, "set", [doId], reportDate, {
            [doId]: row.chatJid,
          })

      request.catch((err) => {
        console.error("Failed to toggle annotation:", err)
        toast.error(t("reports.cmrReport.processedUpdateFailed"))
        updateAnnotation(doId, row.annotation)
      })
    },
    [client, updateAnnotation, selectedDate, t],
  )

  const handleToggleUnlinkedProcessed = useCallback(
    (group: UnlinkedGroup) => {
      if (!client) return
      const groupKey = unlinkedGroupKey(group)
      if (!groupKey) return
      const reportDate = format(selectedDate, "yyyy-MM-dd")
      const previous = unlinkedAnnotations[groupKey]
      const isProcessed = !!previous

      // Optimistic update
      const newAnnotation = isProcessed
        ? undefined
        : {
            processedBy: client.jid?.toString() || "",
            processedAt: new Date().toISOString(),
            chatJid: group.chatJid,
            note: "",
          }
      updateUnlinkedAnnotation(groupKey, newAnnotation)

      // Fire and forget, revert on error
      const request = isProcessed
        ? AdHocCommands.cmrUnlinkedAnnotation(
            client,
            "remove",
            [groupKey],
            reportDate,
          )
        : AdHocCommands.cmrUnlinkedAnnotation(
            client,
            "set",
            [groupKey],
            reportDate,
            { [groupKey]: group.chatJid },
          )

      request.catch((err) => {
        console.error("Failed to toggle unlinked annotation:", err)
        toast.error(t("reports.cmrReport.processedUpdateFailed"))
        updateUnlinkedAnnotation(groupKey, previous)
      })
    },
    [client, unlinkedAnnotations, updateUnlinkedAnnotation, selectedDate, t],
  )

  const isToday =
    format(selectedDate, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd")

  return (
    <>
      <div className="flex h-full flex-col bg-background">
        <div className="flex-1 overflow-auto">
          <div className="p-4">
            <div className="mb-6 flex items-center justify-between">
              <h1 className="text-2xl font-bold">
                {t("reports.cmrReport.title")}
              </h1>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={goToPreviousDay}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="min-w-[140px] rounded-md px-2 py-1 text-center text-sm font-medium hover:bg-accent">
                      {format(selectedDate, "dd MMMM yyyy", {
                        locale: getDateFnsLocale(),
                      })}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="center">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(day) => {
                        if (day) setSelectedDate(day)
                      }}
                      disabled={{ after: new Date() }}
                      defaultMonth={selectedDate}
                    />
                  </PopoverContent>
                </Popover>
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={goToNextDay}
                  disabled={isToday}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                {!isToday && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedDate(new Date())}
                  >
                    {t("common.today")}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refetch}
                  disabled={isLoading}
                >
                  <RefreshCw
                    className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                  />
                </Button>
              </div>
            </div>

            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-lg text-muted-foreground">
                  {t("reports.cmrReport.loading")}
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center py-12">
                <div className="text-lg text-red-500">
                  {t("reports.shared.error", { error })}
                </div>
              </div>
            )}

            {!isLoading && !error && (
              <LinkedUnlinkedTabs
                data={data}
                unlinkedDocuments={unlinkedDocuments}
                unlinkedAnnotations={unlinkedAnnotations}
                onNavigateToChat={handleNavigateToChat}
                onImageClick={preview.openImage}
                onPDFClick={preview.openPdf}
                onToggleProcessed={readOnly ? undefined : handleToggleProcessed}
                onToggleUnlinkedProcessed={
                  readOnly ? undefined : handleToggleUnlinkedProcessed
                }
              />
            )}
          </div>
        </div>
      </div>
      {preview.galleryVisible && preview.ownsGallery && (
        <ImageGallery {...preview.galleryProps} onLinked={refetch} />
      )}
      {preview.pdfVisible && preview.ownsPdf && (
        <PDFViewer {...preview.pdfProps} onLinked={refetch} />
      )}
    </>
  )
}
